// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The playfield's mechanisms (M3-05).
//
// A device is the half of the machine that moves, so each one built here comes with an
// *animator*: a closure that poses its meshes from the simulation's own state. There is no
// second copy of the mechanism's logic in this file. A drop target is down because
// `DropTarget.isUp` says so, a spinner is at the angle the spinner is at, and a bumper's ring
// is down because the coil fired eleven steps ago. Nothing here decides anything.
//
// The animators take `(alpha, stepIndex)` and no time at all. Continuous state — an angle, a
// position — interpolates between the two the fixed step produced, exactly as the ball does
// (PH-022). Discrete state — a coil that fired, a target that dropped — is a *stamp*, and the
// animation is a function of `stepIndex − stamp`, which is a step count and therefore the
// same on a phone at 30 fps as on a desktop at 144. A wall clock anywhere in this file would
// make the machine look different at different frame rates, which is the one thing the fixed
// step exists to prevent.
//
// Geometry sits on the collider, never near it. A slingshot's rubber is drawn along the exact
// segment the solver tests, a bumper's skirt at exactly `skirtRadius`, a target's face on its
// own `a → b`. A ball that appears to bounce off nothing then means the machine is wrong,
// which is a bug you can find, rather than the art being offset, which is a bug you cannot.
//
// ONE DEVICE AT A TIME (M13-03)
// -----------------------------
// Until M13 the only way in was `buildDevices(machine)`, which walked a whole `LoadedMachine`
// and read the live simulation objects as it went — so a device could not be drawn without a
// world to simulate it in, and a flipper without a runtime was not drawn at all. Build mode
// needs a part on a tray before it belongs to any machine, and a scene that can swap one
// device without rebuilding twenty-seven others. So the geometry is now built per device by
// `buildDeviceMesh`, from the *definition* alone, and the runtime is an optional second
// argument that adds the animator: the same meshes with or without it, which is what lets a
// part on a plinth and a part on the playfield be the same object. `buildDevices` composes
// the per-device builds and draws exactly what it always drew.
//
// Every drawn device is one `Group` named by its id, so a scene can add and remove it as one
// object. The parts that used to sit directly under the devices group — a spinner's brackets,
// a gate's bracket, a captive ball's rails — now sit inside their device's group at the same
// world transform, which is the only change to the picture.

import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  RingGeometry,
  SphereGeometry,
  TorusGeometry,
  type Material,
} from 'three'
import type {
  LoadedMachine,
  MachineCoil,
  MachineDevice,
  MachineDefinition,
  PopBumperDevice,
  SlingshotDevice,
  SpinnerDevice,
  TargetDevice,
  KickerDevice,
  GateDevice,
  RolloverDevice,
  CaptiveBallDevice,
  FlipperDevice,
} from '@core/machine'
import type {World} from '@core/sim/World'
import {gateContactPose,rolloverContactPose} from './mechanismBallClearance'
import type { Device } from '@core/sim/devices'
import type { CaptiveBall } from '@core/sim/devices/CaptiveBall'
import type { DropTarget } from '@core/sim/devices/DropTarget'
import type { Gate } from '@core/sim/devices/Gate'
import type { PopBumper } from '@core/sim/devices/PopBumper'
import type { Slingshot } from '@core/sim/devices/Slingshot'
import type { Spinner } from '@core/sim/devices/Spinner'
import type { Trigger } from '@core/sim/devices/Trigger'
import type { Flipper } from '@core/sim/Flipper'
import { BALL_RADIUS } from '@core/sim/constants'
import { lerp } from '../engine/SimulationDriver'
import { PartPalette, railMesh, uprightCylinder } from './partsKit'
import { pulse, settle, since } from './mechanismTiming'
import { authoredBumper, authoredTarget, authoredHardwareFor } from './authoredHardware'
import { withAuthoredLowerAssembly } from './authoredLowerAssembly'
import { withSlingAssembly } from './slingAssembly'
import { withAuthoredFlippers } from './authoredFlippers'
import { withAuthoredSpinners } from './authoredSpinners'
import type {EditorPart} from '../builder/parts'
import {flasherPart,flasherMountFits,flasherFloorHeightScale,FLASHER_MOUNT_HEIGHT} from './flasherAssembly'

/**
 * Pose one mechanism for one frame.
 *
 * `alpha` is the driver's interpolation fraction between the previous simulation state and
 * the current one; `stepIndex` is the current one's index. Both come from the simulation and
 * neither is a duration.
 */
export type DeviceAnimator = (alpha: number, stepIndex: number) => void

export interface BuiltDevices {
  readonly group: Group
  /** Geometries the caller must dispose. Materials belong to the palette. */
  readonly geometries: readonly BufferGeometry[]
  readonly animators: readonly DeviceAnimator[]
  /** Devices drawn as nothing, each with the reason. Reported rather than silently absent. */
  readonly skipped: readonly string[]
  /**
   * The same build, one device at a time, in the definition's order (M13-04).
   *
   * `geometries`, `animators` and `skipped` above are these entries flattened; a scene that
   * wants to swap one device later keeps the entries and ignores the flat lists.
   */
  readonly each: readonly { readonly device: MachineDevice; readonly built: BuiltDevice }[]
}

/**
 * What one device needs from a loaded machine to *move*. Nothing, to merely be drawn.
 *
 * A flipper animates from its `Flipper`; every other mechanism from its simulation `Device`;
 * and a coil-driven one reads its stroke from the definition's `coils`. All three are optional
 * because a part on Build mode's tray has none of them, and it has to look exactly like the
 * part it will become when it is placed.
 */
export interface DeviceRuntime {
  readonly world?:World
  readonly flipper?: Flipper
  readonly device?: Device
  readonly coils?: readonly MachineCoil[]
}

/** One device, built on its own. */
export interface BuiltDevice {
  /** The device's single object, named by its id. `undefined` when it draws as nothing. */
  readonly group: Group | undefined
  /** Geometries the caller must dispose. Materials belong to the palette. */
  readonly geometries: readonly BufferGeometry[]
  /** Present only when a runtime was supplied and the mechanism moves. */
  readonly animator: DeviceAnimator | undefined
  /** Why the device draws as nothing, when it does. */
  readonly skipped: string | undefined
  /**
   * Point the animator at another machine's copy of this device (M13-10).
   *
   * **The same bytes loaded twice are two object graphs, and the renderer will draw the one
   * nobody simulates.** Build mode rebuilds the session on every commit of the draft, so a
   * device whose *definition* did not change — and whose geometry therefore need not be rebuilt
   * — is still animated by a `Flipper` or `Device` that belongs to a world nothing steps any
   * more. This is the seam that stops it: the animator reads its runtime through the builder
   * rather than capturing it, and a rebind swaps the runtime under it without touching a
   * vertex. A no-op for a device that draws nothing.
   */
  readonly rebind: (runtime: DeviceRuntime) => void
}

/** The runtime a loaded machine holds for one of its devices. */
export function runtimeFor(machine: LoadedMachine, device: MachineDevice): DeviceRuntime {
  const runtime: { flipper?: Flipper; device?: Device; coils?: readonly MachineCoil[]; world?:World } = {world:machine.world}
  const flipper = machine.flippers.get(device.id)
  if (flipper !== undefined) runtime.flipper = flipper
  const simulated = machine.devices.get(device.id)
  if (simulated !== undefined) runtime.device = simulated
  if (machine.definition.coils !== undefined) runtime.coils = machine.definition.coils
  return runtime
}

/**
 * Build one device from its definition, and — given its runtime — an animator for it.
 *
 * The geometry is a function of the definition alone, so the same part draws identically on a
 * plinth, in a hand and on the playfield; only whether it *moves* depends on the second
 * argument.
 */
export function buildDeviceMesh(
  device: EditorPart,
  palette: PartPalette,
  runtime: DeviceRuntime = {},
  illustrated = false,
  authored = false,
  definition?:MachineDefinition,
): BuiltDevice {
  if(device.kind==='flasher'){
    const fitted=definition!==undefined&&definition.lamps?.some(l=>l.id===device.id)&&flasherMountFits(definition,device)
    const group=new Group();group.name=device.id;group.position.set(...device.position,fitted?FLASHER_MOUNT_HEIGHT:0)
    const geometries:BufferGeometry[]=[]
    for(const role of fitted?['housing','steel','lens','diffuser','bracket']:['housing','steel','lens','diffuser']){const wall=definition?.colliders.find(c=>c.id==='wallTop');const geometry=flasherPart(role,wall?.kind==='segment'?wall.a[1]-device.position[1]:.045);if(!geometry)continue;geometry.scale((device.radius??.026)/.026,role==='bracket'?1:(device.radius??.026)/.026,fitted?1:flasherFloorHeightScale(device.radius??.026));geometries.push(geometry);const mesh=new Mesh(geometry,palette.get(role==='housing'?'coilArm':(role==='steel'||role==='bracket')?'hardwareSteel':'lampLens'));mesh.name=device.id+'.flasher.authored.'+role;group.add(mesh)}
    return {group,geometries,animator:undefined,skipped:geometries.length===(fitted?5:4)?undefined:'Flasher source is not prepared',rebind:()=>{}}
  }
  const result = new DeviceBuilder(device, palette, runtime, illustrated, authored).build()
  const deck = definition?.layers.find(l => l.id === device.layer && l.kind === 'plane')
  if (result.group !== undefined && deck?.kind === 'plane') result.group.position.z += deck.z
  return result
}

/** Build every device the machine declares, and an animator for each one that moves. */
export function buildDevices(machine: LoadedMachine, palette: PartPalette, illustrated = false, steel?: Material): BuiltDevices {
  const group = new Group()
  group.name = 'devices'
  const geometries: BufferGeometry[] = []
  const animators: DeviceAnimator[] = []
  const skipped: string[] = []
  const each: { device: MachineDevice; built: BuiltDevice }[] = []
  for (const device of machine.definition.devices) {
    const runtime = runtimeFor(machine, device)
    const built = withSlingAssembly(withAuthoredSpinners(withAuthoredFlippers(withAuthoredLowerAssembly(buildDeviceMesh(device, palette, runtime, illustrated,
      illustrated && authoredHardwareFor(machine.definition.machineId),machine.definition), device, machine.definition, palette, illustrated),
      device, machine.definition, palette, illustrated, runtime, steel),
      device, machine.definition, palette, illustrated, runtime),device,machine.definition,palette)
    if (built.group !== undefined) group.add(built.group)
    geometries.push(...built.geometries)
    if (built.animator !== undefined) animators.push(built.animator)
    if (built.skipped !== undefined) skipped.push(built.skipped)
    each.push({ device, built })
  }
  return { group, geometries, animators, skipped, each }
}

class DeviceBuilder {
  /** The device's own object. Every part goes under it, so a scene can hold it as one thing. */
  private readonly group = new Group()
  private assembly?: Group
  private readonly geometries: BufferGeometry[] = []
  private animator: DeviceAnimator | undefined
  private skipped: string | undefined

  /**
   * The runtime the animator reads — *mutable*, and read on every frame rather than captured,
   * so `BuiltDevice.rebind` can point a drawn device at another world's copy of it.
   */
  private live: DeviceRuntime

  constructor(
    private readonly device: MachineDevice,
    private readonly palette: PartPalette,
    runtime: DeviceRuntime,
    private readonly illustrated = false,
    private readonly authored = false,
  ) {
    this.group.name = device.id
    this.live = runtime
  }

  build(): BuiltDevice {
    this.add(this.device)
    if (this.skipped !== undefined) {
      return { group: undefined, geometries: [], animator: undefined, skipped: this.skipped, rebind: () => {} }
    }
    return {
      group: this.assembly ?? this.group,
      geometries: this.geometries,
      animator: this.animator,
      skipped: undefined,
      rebind: (runtime) => {
        this.live = runtime
      },
    }
  }

  /**
   * How long this device's coil is energised, in steps (M9-45, 04 §9).
   *
   * **`coils[].pulseMs` is validated 1…5000 ms in both cores and was read by nothing**, so a
   * machine that declared a 40 ms kicker and a 24 ms bumper got the same hard-coded stroke as one
   * that declared neither: the field was a promise the renderer never kept.
   *
   * `pulseMs` *is* a step count, with no conversion, because the simulation runs at a fixed
   * 1000 Hz — this file's own header says a step is a millisecond and that is the whole reason
   * these animations are measured in steps rather than seconds.
   *
   * The fallback is the constant that was there before, so a machine that declares nothing looks
   * exactly as it did. Only the *out* stroke: the return is a spring, and a spring's travel is a
   * property of the mechanism rather than of how long the coil was on.
   */
  private strokeOf(device: { readonly coil?: string }, fallback: number): number {
    if (device.coil === undefined) return fallback
    const coil = this.live.coils?.find((c) => c.id === device.coil)
    return coil?.pulseMs ?? fallback
  }

  /**
   * The simulation object for this device, when the caller supplied one.
   *
   * Read by every animator on every frame, never captured: see `BuiltDevice.rebind`.
   */
  private simulated<T>(): T | undefined {
    return this.live.device as T | undefined
  }

  private add(device: MachineDevice): void {
    switch (device.kind) {
      case 'flipper':
        return this.flipper(device)
      case 'popBumper':
        return this.popBumper(device)
      case 'slingshot':
        return this.slingshot(device)
      case 'dropTarget':
      case 'standupTarget':
        return this.target(device)
      case 'spinner':
        return this.spinner(device)
      case 'gate':
      case 'ballSaveGate':
      case 'diverter':
        return this.gate(device)
      case 'kicker':
      case 'plunger':
      case 'autoPlunger':
        return this.kicker(device)
      case 'rollover':
        return this.rollover(device)
      case 'captiveBall':
        return this.captiveBall(device)
      case 'magnet':
        // Correct, not missing. A playfield magnet is a coil bolted to the *underside* of the
        // wood; the only thing a player ever sees is the ball behaving strangely above it.
        this.skipped = `${device.id}: a magnet lives under the playfield and has no body`
        return
      case 'dropTargetBank':
        // A bank is a rule about three targets, not a fourth object. Its members drew
        // themselves.
        this.skipped = `${device.id}: a bank is a grouping of targets that draw themselves`
        return
    }
  }

  private own(...geometries: BufferGeometry[]): void {
    this.geometries.push(...geometries)
  }

  // --- flippers ---------------------------------------------------------------

  /**
   * A bat, drawn as the solver models it: a tapered capsule from pivot to tip.
   *
   * Posed from the `Flipper` when there is one, because a bat's angle is the single
   * most-watched number in the machine and the definition only states where it starts; drawn
   * from the definition alone otherwise, at rest, which is what a bat on a tray is. The
   * dimensions are the definition's in both cases — the loader hands the `Flipper` exactly
   * these numbers, so the two cannot disagree.
   */
  private flipper(device: FlipperDevice): void {
    const flipper = this.live.flipper
    const length = device.length
    const radiusPivot = device.radiusPivot
    const radiusTip = device.radiusTip

    const bat = new CylinderGeometry(radiusTip, radiusPivot, length, 16, 1, false)
    // Cylinders are built along their own +y; a bat lies along the playfield's +x at angle 0,
    // and its origin is the *pivot* rather than its centre, so it shifts half a length.
    bat.rotateZ(-Math.PI / 2)
    bat.translate(length / 2, 0, 0)
    this.own(bat)

    const group = this.group
    group.add(new Mesh(bat, this.palette.get('rubber')))

    // The pivot boss and the tip cap: the two ends of a real bat are round, and a bare
    // truncated cone reads as a wedge of cheese from the player camera.
    const boss = uprightCylinder(radiusPivot * 0.55, radiusPivot * 0.62, 0.018, -0.006)
    this.own(boss)
    group.add(new Mesh(boss, this.palette.get('chrome')))

    const tip = new SphereGeometry(radiusTip, 12, 8)
    tip.translate(length, 0, 0)
    this.own(tip)
    group.add(new Mesh(tip, this.palette.get('rubber')))

    group.position.set(device.pivot[0], device.pivot[1], radiusPivot)
    group.rotation.z = device.restAngle

    if (flipper === undefined) return
    this.animator = (alpha) => {
      const live = this.live.flipper
      if (live === undefined) return
      group.rotation.z = lerp(live.prevAngle, live.angle, alpha)
    }
  }

  // --- pop bumpers ------------------------------------------------------------

  /**
   * A pop bumper: a lit base ring, a skirt at the collider's own radius, and a body that is
   * driven down when the coil fires.
   *
   * The skirt is the part the ball touches, so it is a cylinder of exactly `skirtRadius` and
   * tall enough to cover the ball's equator. The rest is above the ball's head and is free to
   * be any shape at all.
   */
  private popBumper(device: PopBumperDevice): void {
    const runtime = this.simulated<PopBumper>()
    const r = device.skirtRadius

    const group = this.group
    group.position.set(device.centre[0], device.centre[1], 0)

    const ring = new RingGeometry(r * 0.78, r * 1.02, 28)
    this.own(ring)
    const ringMesh = new Mesh(ring, this.palette.get('lampLens'))
    ringMesh.position.z = 0.0008
    group.add(ringMesh)

    const skirt = uprightCylinder(r, r, 0.021, 0.001, 24)
    this.own(skirt)
    const skirtMesh = new Mesh(skirt, this.palette.get('plastic'))
    skirtMesh.name = `${device.id}.skirt`
    group.add(skirtMesh)

    // Body and cap ride together on the coil's plunger, so they hang off one sub-group.
    const driven = new Group()
    driven.name = `${device.id}.driven`
    const body = uprightCylinder(r * 0.42, r * 0.52, 0.036, 0.022, 16)
    this.own(body)
    driven.add(new Mesh(body, this.palette.get('chrome')))

    const cap = uprightCylinder(r * 0.88, r * 0.5, 0.016, 0.058, this.illustrated ? 28 : 20)
    this.own(cap)
    const capMesh = new Mesh(cap, this.palette.get(this.illustrated ? 'printedPlastic' : 'lampLens'))
    capMesh.name = `${device.id}.cap`
    driven.add(capMesh)
    if (this.illustrated) {
      const rim = new RingGeometry(r * .79, r * .88, 28)
      const bolt = uprightCylinder(.0023, .0023, .002, .075, 8)
      this.own(rim, bolt)
      const rimMesh = new Mesh(rim, this.palette.get('chrome')); rimMesh.position.z = .0742
      driven.add(rimMesh, new Mesh(bolt, this.palette.get('chrome')))
    }
    group.add(driven)

    if (this.authored) authoredBumper(group, driven, r, device.id, this.palette, geometry => this.own(geometry))

    if (runtime === undefined) return

    // The ring slams down and comes back. `firedAtStep` is a step index, so the whole
    // animation is a step count and identical at every frame rate.
    const down = this.strokeOf(device, BUMPER_DOWN)
    this.animator = (_alpha, step) => {
      const live = this.simulated<PopBumper>()
      if (live === undefined) return
      driven.position.z = -BUMPER_THROW * pulse(since(step, live.firedAtStep), down, BUMPER_UP)
    }
  }

  // --- slingshots -------------------------------------------------------------

  /**
   * A slingshot: rubber stretched between two posts, with the coil's arm behind it.
   *
   * **The rubber does not move.** A real band snaps outward by a few millimetres and this one
   * could be made to, but the band is drawn on the collider the solver tests, and a collider
   * that is somewhere else for forty milliseconds of every kick is exactly the kind of small
   * lie that makes a later physics bug unfindable. The arm has no collider, so the arm is what
   * moves — which is also what a player actually sees through the plastics.
   */
  private slingshot(device: SlingshotDevice): void {
    const runtime = this.simulated<Slingshot>()
    const [ax, ay] = device.a
    const [bx, by] = device.b
    const dx = bx - ax
    const dy = by - ay
    const len = Math.hypot(dx, dy)
    if (len === 0) {
      this.skipped = `${device.id}: degenerate slingshot face`
      return
    }
    // The face's outward normal: the left-hand perpendicular of a → b, which is the same
    // default the solver applies when a slingshot states no normal of its own.
    const nx = -dy / len
    const ny = dx / len

    const group = this.group

    const band = railMesh([device.a, device.b], this.palette.get('rubber'), 0.019, 0.0035, 0.004)
    band.name = `${device.id}.rubber`
    this.own(band.geometry)
    group.add(band)

    for (const [px, py] of [device.a, device.b]) {
      const post = uprightCylinder(0.0055, 0.006, 0.032, 0)
      this.own(post)
      const mesh = new Mesh(post, this.palette.get('chrome'))
      mesh.position.set(px, py, 0)
      group.add(mesh)
    }

    const arm = new BoxGeometry(len * 0.62, 0.005, 0.015)
    arm.translate(0, 0, 0.0135)
    this.own(arm)
    const armMesh = new Mesh(arm, this.palette.get('coilArm'))
    armMesh.rotation.z = Math.atan2(dy, dx)
    const restX = (ax + bx) / 2 - nx * SLING_ARM_SETBACK
    const restY = (ay + by) / 2 - ny * SLING_ARM_SETBACK
    armMesh.name = `${device.id}.arm`
    armMesh.position.set(restX, restY, 0)
    group.add(armMesh)

    if (runtime === undefined) return
    const thrown = this.strokeOf(device, SLING_OUT)
    this.animator = (_alpha, step) => {
      const live = this.simulated<Slingshot>()
      if (live === undefined) return
      const out = SLING_ARM_THROW * pulse(since(step, live.firedAtStep), thrown, SLING_BACK)
      armMesh.position.set(restX + nx * out, restY + ny * out, 0)
    }
  }

  // --- targets ----------------------------------------------------------------

  /**
   * A target: a lit face standing on the wood along its own `a → b`.
   *
   * A drop target sinks below the surface when it drops and rises when the bank resets. The
   * simulation makes that switch between one step and the next — a face that is half there is
   * neither solid nor passable, and there is no honest answer for a ball that hits it — so the
   * seventy milliseconds the mechanism takes are drawn here and nowhere else.
   */
  private target(device: TargetDevice): void {
    const face = railMesh([device.a, device.b], this.palette.get('targetFace'), 0.026, 0.0022)
    face.name = `${device.id}.face`
    this.own(face.geometry)

    const group = this.group
    group.add(face)

    if (this.authored) this.assembly = authoredTarget(group, device.a, device.b, device.id,
      this.palette, geometry => this.own(geometry))

    if (device.kind !== 'dropTarget') return
    const runtime = this.simulated<DropTarget>()
    if (runtime === undefined) return

    this.animator = (_alpha, step) => {
      const live = this.simulated<DropTarget>()
      if (live === undefined) return
      // `settle` runs 0 → 1 over the travel time; a target that has been down for a while
      // reads 1 and stays there, which is what a stamp rather than a countdown buys.
      const moved = settle(since(step, live.movedAtStep), TARGET_TRAVEL)
      const fraction = live.isUp ? 1 - moved : moved
      group.position.z = -TARGET_DROP * fraction
    }
  }

  // --- spinners ---------------------------------------------------------------

  /**
   * A spinner: a vane hanging from a shaft between two brackets.
   *
   * Two nested groups, because the vane turns about the *shaft*, not about anything in the
   * playfield frame. The outer group carries the shaft's bearing, so inside it the shaft is
   * local +x and the vane's whole motion is one rotation about that axis — matching the
   * simulation, where `angle` is measured from hanging straight down and positive turns
   * toward the shaft's left-hand perpendicular.
   */
  private spinner(device: SpinnerDevice): void {
    const runtime = this.simulated<Spinner>()
    const [ax, ay] = device.a
    const [bx, by] = device.b
    const dx = bx - ax
    const dy = by - ay
    const len = Math.hypot(dx, dy)
    if (len === 0) {
      this.skipped = `${device.id}: degenerate spinner shaft`
      return
    }

    for (const [px, py] of [device.a, device.b]) {
      const bracket = uprightCylinder(0.0022, 0.003, device.shaftHeight + 0.004, 0)
      this.own(bracket)
      const mesh = new Mesh(bracket, this.palette.get('chrome'))
      mesh.position.set(px, py, 0)
      this.group.add(mesh)
    }

    const shaftFrame = new Group()
    shaftFrame.name = `${device.id}.shaft`
    shaftFrame.position.set((ax + bx) / 2, (ay + by) / 2, device.shaftHeight)
    shaftFrame.rotation.z = Math.atan2(dy, dx)
    this.group.add(shaftFrame)

    const shaft = new CylinderGeometry(0.0011, 0.0011, len, 8, 1, false)
    shaft.rotateZ(Math.PI / 2)
    this.own(shaft)
    shaftFrame.add(new Mesh(shaft, this.palette.get('chrome')))

    const hinge = new Group()
    hinge.name = `${device.id}.vane`
    shaftFrame.add(hinge)

    const vane = new BoxGeometry(len - 0.008, 0.0009, device.vaneDrop)
    // Hinged along its top edge, so it hangs below the shaft rather than straddling it.
    vane.translate(0, 0, -device.vaneDrop / 2)
    this.own(vane)
    hinge.add(new Mesh(vane, this.palette.get('lampLens')))

    if (runtime === undefined) return
    this.animator = (alpha) => {
      const live = this.simulated<Spinner>()
      if (live === undefined) return
      hinge.rotation.x = lerp(live.prevAngle, live.angle, alpha)
    }
  }

  // --- gates ------------------------------------------------------------------

  /**
   * A one-way gate: an arm hinged at `a`, lying along `a → b` when shut and lifting out of
   * the playfield when a ball pushes through in the permitted direction.
   *
   * The lift is a rotation about the horizontal axis through `a` perpendicular to the arm,
   * which is why the arm hangs off a group carrying the arm's bearing: inside it the arm is
   * local +x and lifting is one rotation about local −y.
   */
  private gate(device: GateDevice): void {
    const runtime = this.simulated<Gate>()
    const [ax, ay] = device.a
    const [bx, by] = device.b
    const dx = bx - ax
    const dy = by - ay
    const len = Math.hypot(dx, dy)
    if (len === 0) {
      this.skipped = `${device.id}: degenerate gate arm`
      return
    }

    const bracket = uprightCylinder(0.0026, 0.0032, 0.034, 0)
    this.own(bracket)
    const bracketMesh = new Mesh(bracket, this.palette.get('chrome'))
    bracketMesh.position.set(ax, ay, 0)
    this.group.add(bracketMesh)

    const bearing = new Group()
    bearing.name = `${device.id}.bearing`
    bearing.position.set(ax, ay, GATE_HINGE_HEIGHT)
    bearing.rotation.z = Math.atan2(dy, dx)
    this.group.add(bearing)

    // The lift is its own child group rather than a second angle on `bearing`. Three composes
    // a single `Euler` in one fixed order, so a `rotation.y` written beside a `rotation.z`
    // turns about the axis the *parent* calls y — not about the arm — and a gate whose arm
    // happens to run along +x looks perfect while every other gate on the machine lifts
    // sideways into the wood. Nesting makes the axis local by construction.
    const lift = new Group()
    bearing.add(lift)

    const rod = new CylinderGeometry(0.0013, 0.0013, len, 8, 1, false)
    rod.rotateZ(Math.PI / 2)
    rod.translate(len / 2, 0, 0)
    this.own(rod)
    lift.add(new Mesh(rod, this.palette.get('wire')))

    // The blade below the rod is what the ball actually meets, so it reaches the wood.
    const blade = new BoxGeometry(len, 0.0008, GATE_HINGE_HEIGHT)
    blade.translate(len / 2, 0, -GATE_HINGE_HEIGHT / 2)
    this.own(blade)
    lift.add(new Mesh(blade, this.palette.get('wire')))

    if (runtime === undefined) return
    this.animator = (alpha) => {
      const live = this.simulated<Gate>()
      if (live === undefined) return
      const angle = gateContactPose(this.live.world,ax,ay,bx,by,lerp(live.prevAngle, live.angle, alpha),alpha)
      // Negative, because a positive rotation about local +y drives local +x downward.
      lift.rotation.y = -angle
    }
  }

  // --- kickers, scoops and the shooter ---------------------------------------

  /**
   * A hole in the wood with a coil under it — a scoop, a kickback or the shooter lane.
   *
   * Drawn as a recessed cup rather than a dark disc, because a ball sitting in a scoop has to
   * sit *in* something: a flat disc leaves it apparently balanced on the paint.
   */
  private kicker(device: KickerDevice): void {
    const r = device.captureRadius
    const group = this.group
    group.position.set(device.centre[0], device.centre[1], 0)

    if (this.illustrated && device.kind === 'kicker' && device.holdMs > 0) {
      const cup = new CylinderGeometry(r,r*.72,.026,32,1,true)
      cup.rotateX(Math.PI/2); cup.translate(0,0,-.013)
      const index=cup.index!, normals=cup.getAttribute('normal')
      for(let i=0;i<index.count;i+=3) { const a=index.getX(i); index.setX(i,index.getX(i+2)); index.setX(i+2,a) }
      for(let i=0;i<normals.count;i++) normals.setXYZ(i,-normals.getX(i),-normals.getY(i),-normals.getZ(i))
      const bottom=uprightCylinder(r*.72,r*.72,.0002,-.026,24), rim=new TorusGeometry(r+.0017,.0017,8,32)
      this.own(cup,bottom,rim)
      const rimMesh=new Mesh(rim,this.palette.get('chrome')); rimMesh.position.z=.0017
      group.add(new Mesh(cup,this.palette.get('coilArm')),new Mesh(bottom,this.palette.get('shadow')),rimMesh)
    } else {
      const cup = uprightCylinder(r, r * 0.72, 0.026, -0.026, 20)
      this.own(cup); group.add(new Mesh(cup, this.palette.get('shadow')))
      const rim = new RingGeometry(r, r + 0.0035, 24)
      this.own(rim)
      const rimMesh = new Mesh(rim, this.palette.get('chrome')); rimMesh.position.z = 0.0006
      group.add(rimMesh)
    }

    // **No rod here any more** (14 Sep 2026). This drew a 75 mm stub down the lane with a rubber
    // tip 94 mm below the device's centre — past the playfield's own front edge — while the
    // cabinet drew a level rod through the front at a height of its own, and the two met nowhere:
    // "pinball plunger looks odd, doesn't line up properly with the inside plunger and outside
    // plunger". The shooter rod is one piece, tip to knob, along the lane's axis, and the cabinet
    // draws all of it (`cabinet.ts`, `controls.plunger.tip`). The cup and the rim are the lane's.
  }

  // --- rollovers --------------------------------------------------------------

  /**
   * A rollover: a nonblocking scoring switch whose wire depresses under the ball.
   *
   * The arch spans the region across the playfield's `x`, because that is the direction a
   * ball crosses a lane in every case NEON NOVA has — its lanes, inlanes, outlanes and orbit
   * return all run along `y`. A machine whose rollover sits in a sideways lane would want the
   * other orientation, and the format has nowhere to say so yet; when a machine needs it,
   * that is a field in the definition rather than a guess made here.
   */
  private rollover(device: RolloverDevice): void {
    const region = device.region
    const span = region.shape === 'circle' ? region.radius : (region.max[0] - region.min[0]) / 2
    const centre: readonly [number, number] =
      region.shape === 'circle'
        ? region.centre
        : [(region.min[0] + region.max[0]) / 2, (region.min[1] + region.max[1]) / 2]

    // The wire rises from the wood on one side of the lane and comes back down on the other.
    const wire = archGeometry(span, ROLLOVER_HEIGHT)
    this.own(wire)
    const mesh = new Mesh(wire, this.palette.get('wire'))
    mesh.position.set(centre[0], centre[1], 0)
    mesh.name = `${device.id}.wire`
    this.group.add(mesh)
    if (this.simulated<Trigger>() === undefined) return
    this.animator = (alpha, step) => {
      const runtime = this.simulated<Trigger>()
      const elapsed = since(step, runtime?.closedAtStep ?? -1)
      // Hold a brief visible stroke even if a fast pass closes and opens between frames.
      const down = runtime?.isClosed ? 1 : elapsed < 0 ? 0 : 1 - settle(Math.max(0, elapsed - ROLLOVER_HOLD), ROLLOVER_RETURN)
      const context=mesh.parent,span=Number(mesh.userData['rolloverSpan']??0),depth=Number(mesh.userData['rolloverDepth']??0)
      mesh.rotation.x = context&&span>0?rolloverContactPose(this.live.world,context.position.x,context.position.y,context.rotation.z,span,depth,ROLLOVER_TURN*down,alpha):ROLLOVER_TURN*down
    }
  }

  // --- captive balls ----------------------------------------------------------

  /** A captive ball in its channel, drawn from the simulation's own position for it. */
  private captiveBall(device: CaptiveBallDevice): void {
    const runtime = this.simulated<CaptiveBall>()

    const sphere = new SphereGeometry(BALL_RADIUS, 24, 12)
    this.own(sphere)
    const mesh = new Mesh(sphere, this.palette.get('chrome'))
    mesh.name = `${device.id}.ball`
    mesh.position.set(device.centre[0], device.centre[1], BALL_RADIUS)
    this.group.add(mesh)

    // The channel it runs in: a rail either side of the travel, so the ball is visibly
    // captive rather than loose on the playfield.
    const [ux, uy] = unit(device.direction)
    const half = device.travel / 2 + BALL_RADIUS
    for (const side of [1, -1]) {
      const ox = -uy * (BALL_RADIUS + 0.0025) * side
      const oy = ux * (BALL_RADIUS + 0.0025) * side
      const rail = railMesh(
        [
          [device.centre[0] - ux * half + ox, device.centre[1] - uy * half + oy],
          [device.centre[0] + ux * half + ox, device.centre[1] + uy * half + oy],
        ],
        this.palette.get('metal'),
        0.028,
      )
      this.own(rail.geometry)
      this.group.add(rail)
    }

    if (runtime === undefined) return
    this.animator = () => {
      const live = this.simulated<CaptiveBall>()
      if (live === undefined) return
      mesh.position.set(live.x, live.y, BALL_RADIUS)
    }
  }
}

/**
 * A wire arch: a half-circle of radius `span` in the x–z plane, swept as a thin tube.
 *
 * Three's `TorusGeometry` would nearly do it, but a torus is swept about an axis and a
 * rollover wire is swept about a point on the wood: the tube has to stay a tube where the
 * arch meets the surface, which a partial torus does not. Sixteen segments of a six-sided
 * tube is cheaper to state directly than to correct.
 */
function archGeometry(span: number, height: number): BufferGeometry {
  const segments = 16
  const sides = 6
  const radius = 0.0011

  /** One point on the tube's surface: `k` along the arch, `t` around the cross-section. */
  const at = (k: number, t: number): readonly [number, number, number] => {
    const a = (Math.PI * k) / segments
    const cx = -span * Math.cos(a)
    const cz = height * Math.sin(a)
    // The arch lies in x–z, so the cross-section spans y and the arch's own outward normal,
    // which at parameter `a` points along (−cos a, 0, sin a) scaled to the ellipse's axes.
    const ox = Math.sin(a) * radius * Math.cos(t)
    const oz = Math.cos(a) * radius * Math.cos(t)
    return [cx + ox, radius * Math.sin(t), cz + oz]
  }

  const positions = new Float32Array(segments * sides * 18)
  let w = 0
  const push = (p: readonly [number, number, number]): void => {
    positions[w++] = p[0]
    positions[w++] = p[1]
    positions[w++] = p[2]
  }
  for (let i = 0; i < segments; i++) {
    for (let s = 0; s < sides; s++) {
      const t0 = (2 * Math.PI * s) / sides
      const t1 = (2 * Math.PI * (s + 1)) / sides
      const p00 = at(i, t0)
      const p01 = at(i, t1)
      const p10 = at(i + 1, t0)
      const p11 = at(i + 1, t1)
      push(p00)
      push(p10)
      push(p11)
      push(p00)
      push(p11)
      push(p01)
    }
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.computeVertexNormals()
  geometry.computeBoundingSphere()
  return geometry
}

function unit(v: readonly [number, number]): readonly [number, number] {
  const len = Math.hypot(v[0], v[1])
  if (len === 0) return [0, 1]
  return [v[0] / len, v[1] / len]
}

/** Millimetres a bumper's ring travels when the coil fires. */
const BUMPER_THROW = 0.009
/**
 * Steps down, then steps back. At 1000 Hz a step is a millisecond.
 *
 * `BUMPER_DOWN` is the **fallback** since M9-45: a machine whose coil declares `pulseMs` is
 * energised for exactly that long and this is what a machine that declares nothing gets.
 */
const BUMPER_DOWN = 18
const BUMPER_UP = 70

const SLING_ARM_SETBACK = 0.011
const SLING_ARM_THROW = 0.008
const SLING_OUT = 12
const SLING_BACK = 55

/** A dropped target sinks a whole face height plus clearance, so nothing shows above the wood. */
const TARGET_DROP = 0.03
const TARGET_TRAVEL = 70

const GATE_HINGE_HEIGHT = 0.03
const ROLLOVER_HEIGHT = 0.022
const ROLLOVER_TURN = 1.43
const ROLLOVER_HOLD = 35
const ROLLOVER_RETURN = 70
